import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {apiFetchPublic, ApiError} from '../api';
import Checklist from './Checklist';
import Spinner from './Spinner';

export default function SharedChecklistPage() {
    const {token} = useParams();
    const [checklist, setChecklist] = useState(null);
    const [accessLevel, setAccessLevel] = useState('read');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        apiFetchPublic(`/s/${token}`)
            .then(res => res.json())
            .then(data => {
                if (cancelled) return;
                setChecklist(data);
                setAccessLevel(data.accessLevel || 'read');
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                if (err instanceof ApiError && err.status === 404) {
                    setError('not_found');
                } else {
                    setError('error');
                }
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleToggleItem = useCallback(
        async (_checklistId, itemId) => {
            try {
                const res = await apiFetchPublic(`/s/${token}/items/${itemId}`, {method: 'PATCH'});
                const updated = await res.json();
                setChecklist(prev => ({
                    ...prev,
                    items: prev.items.map(i => (i.id === itemId ? {...i, checked: updated.checked} : i)),
                }));
            } catch {
                // silently fail — user can retry
            }
        },
        [token],
    );

    if (loading) {
        return (
            <div className="shared-page" data-testid="shared-page">
                <Spinner size={32} className="spinner-fullpage" />
            </div>
        );
    }

    if (error === 'not_found') {
        return (
            <div className="shared-page" data-testid="shared-page">
                <div className="shared-error">
                    <h2>Not available</h2>
                    <p>This checklist is no longer available.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="shared-page" data-testid="shared-page">
                <div className="shared-error">
                    <h2>Something went wrong</h2>
                    <p>Could not load this checklist. Please try again later.</p>
                </div>
            </div>
        );
    }

    const isReadOnly = accessLevel === 'read';
    const noop = () => {};

    return (
        <div className="shared-page" data-testid="shared-page">
            <div className="shared-banner">
                You&rsquo;re viewing a shared checklist
                {!isReadOnly && ' (you can toggle items)'}
            </div>
            <div className="shared-checklist-wrapper">
                <Checklist
                    data={checklist}
                    readOnly={isReadOnly}
                    shareEditMode={!isReadOnly}
                    onToggleItem={handleToggleItem}
                    onDelete={noop}
                    onAddItem={noop}
                    onDeleteItem={noop}
                    onUpdateTitle={noop}
                    onUpdateItem={noop}
                    onReorderItems={noop}
                    onToggleSortMode={noop}
                    onToggleSinkChecked={noop}
                    onToggleHideChecked={noop}
                    onStarItem={noop}
                    onSetDueDateItem={noop}
                    isFocused={false}
                    onFocus={noop}
                    onUnfocus={noop}
                />
            </div>
        </div>
    );
}
